import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { usePracticeKeypointTypes } from '@witmetrics/data-store';
import { currentDate } from '@witmetrics/utils/time';
import { useString } from '@/hooks/useString';
import { useDate } from '@/hooks/useDate';
import Card from '@/components/Card';
import SearchableDropdown, {
  type SearchableDropdownOption,
} from '@/components/inputs/SearchableDropdown';
import DateTimeInput from '@/components/inputs/DateTimeInput';
import TextField from '@/components/inputs/TextField';
import Button from '@/components/Button';
import {
  buildKeypointTypeOptions,
  classes,
  searchKeypointTypes,
} from './utils';

export type KeypointFieldProps = {
  isSaving: boolean;
  className?: string;
  onSave: (data: {
    keypointTypeId: number;
    startsAt: string;
    message: string;
  }) => void;
  onCancel: () => void;
};

export default function KeypointField({
  isSaving,
  className,
  onSave,
  onCancel,
}: KeypointFieldProps) {
  const keypointTypes = usePracticeKeypointTypes();
  const [keypointType, setKeypointType] =
    useState<SearchableDropdownOption<number> | null>(null);
  const [startsAt, setStartsAt] = useDate(currentDate());
  const [message, setMessage] = useString();

  const handleSave = () => {
    if (!(keypointType && startsAt)) return;
    onSave({
      keypointTypeId: keypointType.value,
      startsAt,
      message,
    });
  };

  return (
    <Card className={twMerge(classes.card, className)}>
      <div className={classes.settingsWrapper}>
        <SearchableDropdown<number>
          disabled={isSaving}
          label="Keypoint Type"
          value={keypointType}
          initialOptions={buildKeypointTypeOptions(keypointTypes)}
          onSearch={async (search) =>
            searchKeypointTypes(keypointTypes, search)
          }
          onSelect={setKeypointType}
        />
        <DateTimeInput
          disablePast={false}
          showTime={false}
          className={classes.dateInput}
          label="Date"
          value={startsAt}
          onChange={setStartsAt}
        />
      </div>
      <TextField
        multiline
        minRows={3}
        maxRows={5}
        label="Notes (optional)"
        placeholder="Add notes..."
        value={message}
        onChange={setMessage}
      />
      <div className={classes.buttonsWrapper}>
        <Button className={classes.button} variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <div className={classes.saveWrapper}>
          <Button
            disabled={isSaving || !keypointType}
            className={classes.button}
            onClick={handleSave}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
